/**
 * openFilesStore — multi-file tab strip state, **per project**.
 *
 * Capped at MAX_OPEN_TABS per project (currently 10). When opening tab #11
 * the oldest non-dirty tab is evicted (Chrome-tab/WebStorm style). If all
 * 10 are dirty the cap is honored as a soft limit — we keep the dirty tabs
 * so unsaved work isn't silently dropped.
 *
 * Per-project state survives project switches: switching from project A to
 * project B saves A's tabs in memory; switching back restores them with
 * scroll position and cursor location intact. A debounced write to
 * `.ateliercode/cache/editor-state.json` (driven by `editorStatePersistence.ts`)
 * carries this across app restarts.
 */
import { create } from 'zustand';

export const MAX_OPEN_TABS = 10;

export interface OpenFile {
  /** Project-relative path with forward slashes (matches FileNode.id). */
  id: string;
  /** Absolute filesystem path — what Rust file commands canonicalize. */
  path: string;
  /** Display name (basename). */
  name: string;
  /** Editor has unsaved edits (shown as a dot in the tab). */
  dirty: boolean;
  /** Last time the tab was opened/activated — drives eviction order. */
  lastActiveAt: number;
  /** Last known Monaco scrollTop (px). Restored when this tab re-activates. */
  scrollTop?: number;
  /** Last known Monaco cursor line (1-indexed). */
  cursorLine?: number;
  /** Last known Monaco cursor column (1-indexed). */
  cursorColumn?: number;
}

export interface ProjectTabState {
  open: OpenFile[];
  activeId: string | null;
}

interface OpenFilesState {
  /** The project whose tabs are currently shown. */
  currentProjectId: string | null;
  /** Per-project tab state. Keys are project ids. */
  byProject: Map<string, ProjectTabState>;

  /** Switch the active project. Saves current project's state in place;
   *  restores the new project's state if previously seen. */
  setProjectId: (id: string | null) => void;

  /** Bulk-load a project's tab state (used by cache restore on mount). */
  hydrateProject: (id: string, state: ProjectTabState) => void;

  // ── Operations on the current project's state ──

  /** Open a file (or activate it if already open). Returns the OpenFile. */
  openFile: (file: { id: string; path: string; name: string }) => OpenFile;
  closeFile: (id: string) => void;
  closeAll: () => void;
  setActive: (id: string) => void;
  setDirty: (id: string, dirty: boolean) => void;
  /** Capture editor scroll + cursor for a tab, e.g. just before tab switch. */
  setScrollState: (
    id: string,
    state: { scrollTop?: number; cursorLine?: number; cursorColumn?: number },
  ) => void;

  reset: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Mutate the current project's state via a transform function. No-ops when
 * there is no active project.
 */
function withCurrent(
  state: OpenFilesState,
  transform: (curr: ProjectTabState) => ProjectTabState,
): Partial<OpenFilesState> {
  const id = state.currentProjectId;
  if (!id) return {};
  const curr = state.byProject.get(id) ?? { open: [], activeId: null };
  const next = transform(curr);
  if (next === curr) return {};
  const map = new Map(state.byProject);
  map.set(id, next);
  return { byProject: map };
}

function applyOpenFile(
  curr: ProjectTabState,
  file: { id: string; path: string; name: string },
): { state: ProjectTabState; opened: OpenFile } {
  const now = Date.now();
  const existing = curr.open.find((f) => f.id === file.id);
  if (existing) {
    const updated: OpenFile = { ...existing, lastActiveAt: now };
    return {
      state: {
        open: curr.open.map((f) => (f.id === file.id ? updated : f)),
        activeId: file.id,
      },
      opened: updated,
    };
  }

  const newFile: OpenFile = {
    id: file.id,
    path: file.path,
    name: file.name,
    dirty: false,
    lastActiveAt: now,
  };
  let nextOpen = [...curr.open, newFile];
  if (nextOpen.length > MAX_OPEN_TABS) {
    const oldestNonDirty = nextOpen
      .slice(0, -1)
      .filter((f) => !f.dirty)
      .sort((a, b) => a.lastActiveAt - b.lastActiveAt)[0];
    if (oldestNonDirty) {
      nextOpen = nextOpen.filter((f) => f.id !== oldestNonDirty.id);
    }
    // All-dirty case: keep dirty tabs and let the list exceed the cap.
  }

  return {
    state: { open: nextOpen, activeId: file.id },
    opened: newFile,
  };
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useOpenFilesStore = create<OpenFilesState>((set, get) => ({
  currentProjectId: null,
  byProject: new Map(),

  setProjectId: (id) =>
    set((s) => {
      if (s.currentProjectId === id) return {};
      // No-op for null switches — the caller usually still wants the data
      // around for the next project that comes in.
      return { currentProjectId: id };
    }),

  hydrateProject: (id, state) =>
    set((s) => {
      const map = new Map(s.byProject);
      map.set(id, {
        open: state.open.map((f) => ({ ...f })),
        activeId: state.activeId,
      });
      return { byProject: map };
    }),

  openFile: (file) => {
    const state = get();
    let opened: OpenFile = {
      id: file.id,
      path: file.path,
      name: file.name,
      dirty: false,
      lastActiveAt: Date.now(),
    };
    set(
      withCurrent(state, (curr) => {
        const result = applyOpenFile(curr, file);
        opened = result.opened;
        return result.state;
      }),
    );
    return opened;
  },

  closeFile: (id) =>
    set((s) =>
      withCurrent(s, (curr) => {
        const idx = curr.open.findIndex((f) => f.id === id);
        if (idx < 0) return curr;
        const nextOpen = curr.open.filter((f) => f.id !== id);
        let nextActive = curr.activeId;
        if (curr.activeId === id) {
          nextActive = nextOpen[idx]?.id ?? nextOpen[idx - 1]?.id ?? null;
        }
        return { open: nextOpen, activeId: nextActive };
      }),
    ),

  closeAll: () =>
    set((s) => withCurrent(s, () => ({ open: [], activeId: null }))),

  setActive: (id) =>
    set((s) =>
      withCurrent(s, (curr) => {
        if (!curr.open.some((f) => f.id === id)) return curr;
        return {
          activeId: id,
          open: curr.open.map((f) =>
            f.id === id ? { ...f, lastActiveAt: Date.now() } : f,
          ),
        };
      }),
    ),

  setDirty: (id, dirty) =>
    set((s) =>
      withCurrent(s, (curr) => ({
        ...curr,
        open: curr.open.map((f) => (f.id === id ? { ...f, dirty } : f)),
      })),
    ),

  setScrollState: (id, patch) =>
    set((s) =>
      withCurrent(s, (curr) => ({
        ...curr,
        open: curr.open.map((f) =>
          f.id === id
            ? {
                ...f,
                scrollTop: patch.scrollTop ?? f.scrollTop,
                cursorLine: patch.cursorLine ?? f.cursorLine,
                cursorColumn: patch.cursorColumn ?? f.cursorColumn,
              }
            : f,
        ),
      })),
    ),

  reset: () => set({ currentProjectId: null, byProject: new Map() }),
}));

// ─── Selectors / convenience getters ────────────────────────────────────────

/**
 * Hook returning the open list for the currently-selected project.
 * Empty array if no project is active or the project hasn't opened anything.
 */
export function useCurrentProjectOpenFiles(): OpenFile[] {
  return useOpenFilesStore((s) => {
    const id = s.currentProjectId;
    if (!id) return EMPTY_OPEN;
    return s.byProject.get(id)?.open ?? EMPTY_OPEN;
  });
}

/** Hook returning the active tab id for the currently-selected project. */
export function useCurrentProjectActiveId(): string | null {
  return useOpenFilesStore((s) => {
    const id = s.currentProjectId;
    if (!id) return null;
    return s.byProject.get(id)?.activeId ?? null;
  });
}

/** Stable empty-array reference so the selector doesn't churn equality checks. */
const EMPTY_OPEN: OpenFile[] = [];
